// src/pages/Reports.jsx - Complete Global Reports with MITRE Heatmap and Exports
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, LineElement, PointElement } from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar,
  faFolder,
  faBullseye,
  faCheckCircle,
  faMapMarkedAlt,
  faFlask,
  faSpinner,
  faChartLine,
  faChartPie,
  faFileAlt,
  faClipboardList,
  faUser,
  faTrophy,
  faMedal,
  faAward,
  faEye,
  faChartArea,
  faFilePdf,
  faFileExcel,
  faMap,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import projectService from '../services/projectService';
import actionService from '../services/actionService';
import atomicService from '../services/atomicService';
import exportService from '../services/exportService';
import webFindingService from '../services/webFindingService';
import MitreHeatmap from '../components/mitre/MitreHeatmap';
import AtomicTestViewer from '../components/atomic/AtomicTestViewer';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, LineElement, PointElement);

const Reports = () => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const [projects, setProjects] = useState([]);
  const [actions, setActions] = useState([]);
  const [atomicTests, setAtomicTests] = useState([]);
  const [webFindingsForMitre, setWebFindingsForMitre] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        // Fetch all data
        const projectsData = await projectService.getProjects();
        const actionsData = await actionService.getActions();

        setProjects(Array.isArray(projectsData) ? projectsData : []);
        setActions(Array.isArray(actionsData) ? actionsData : []);

        // Fetch all atomic tests
        try {
          const atomicData = await atomicService.getAtomicTests();
          setAtomicTests(Array.isArray(atomicData) ? atomicData : []);
        } catch (error) {
          console.log('No atomic tests found');
        }

        // Fetch web findings for MITRE cross-mapping (silent fail if none)
        try {
          const wfData = await webFindingService.getAll();
          const findings = Array.isArray(wfData) ? wfData : (wfData?.data || []);
          if (findings.length > 0) {
            // Map web findings to MITRE technique format using wstgTest.mitreMapping
            const mapped = findings
              .filter(f => f.wstgTest?.categoryCode)
              .map(f => ({
                _id: f._id,
                title: f.title,
                severity: f.severity,
                wstgTestId: f.wstgTest?.testId,
                techniqueId: f.mitreTechnique?.id || null,
                techniqueName: f.mitreTechnique?.name || null,
                tactic: f.mitreTechnique?.tactic || null
              }))
              .filter(f => f.techniqueId);
            setWebFindingsForMitre(mapped);
          }
        } catch (error) {
          // No web findings - that's fine
        }

        setLoading(false);
      } catch (error) {
        setError('Failed to fetch report data');
        setLoading(false);
      }
    };

    fetchReportData();
  }, []);

  // Export handlers
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await exportService.exportExecutiveSummary();
      toast.success('Executive summary exported successfully!');
    } catch (error) {
      toast.error('Failed to export executive summary');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await exportService.exportExcel();
      toast.success('Excel data exported successfully!');
    } catch (error) {
      toast.error('Failed to export Excel data');
    } finally {
      setExporting(false);
    }
  };

  const handleExportMitreNavigator = async () => {
    setExporting(true);
    try {
      // Pass actions for client-side generation if backend fails
      await exportService.exportMitreNavigator(null, actions, 'Global Red Team Operations');
      toast.success('MITRE Navigator layer exported successfully!');
    } catch (error) {
      console.error('MITRE Navigator export error:', error);
      toast.error(`Failed to export MITRE Navigator layer: ${error.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportAnalytics = async () => {
    setExporting(true);
    try {
      await exportService.exportAnalytics();
      toast.success('Analytics dashboard exported successfully!');
    } catch (error) {
      toast.error('Failed to export analytics dashboard');
    } finally {
      setExporting(false);
    }
  };

  // Generate comprehensive analytics
  const getGlobalAnalytics = () => {
    if (!actions.length) return null;

    // Project status breakdown
    const projectStatusCount = {
      'planning': 0,
      'in-progress': 0,
      'completed': 0,
      'archived': 0,
    };

    projects.forEach(project => {
      projectStatusCount[project.status] = (projectStatusCount[project.status] || 0) + 1;
    });

    // Action results breakdown
    const actionResultCount = {
      'successful': 0,
      'detected': 0,
      'blocked': 0,
      'failed': 0,
      'partial': 0,
      'unknown': 0,
    };

    // MITRE tactics breakdown
    const tacticCount = {};
    
    // Timeline data (by month)
    const timelineData = {};
    
    // Operator performance
    const operatorStats = {};

    // Project-wise action counts
    const projectActionCount = {};

    actions.forEach(action => {
      // Count results
      if (action.result) {
        actionResultCount[action.result] = (actionResultCount[action.result] || 0) + 1;
      }

      // Count MITRE tactics
      if (action.mitreAttackTechnique && action.mitreAttackTechnique.tactic) {
        tacticCount[action.mitreAttackTechnique.tactic] = 
          (tacticCount[action.mitreAttackTechnique.tactic] || 0) + 1;
      }

      // Timeline data (by month)
      const month = new Date(action.startTimestamp || action.timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      timelineData[month] = (timelineData[month] || 0) + 1;

      // Operator stats
      if (action.operator) {
        if (!operatorStats[action.operator]) {
          operatorStats[action.operator] = { total: 0, successful: 0, detected: 0, projects: new Set() };
        }
        operatorStats[action.operator].total++;
        operatorStats[action.operator].projects.add(action.project._id || action.project);
        if (action.result === 'successful') {
          operatorStats[action.operator].successful++;
        }
        if (action.result === 'detected') {
          operatorStats[action.operator].detected++;
        }
      }

      // Project action counts
      const projectId = action.project._id || action.project;
      projectActionCount[projectId] = (projectActionCount[projectId] || 0) + 1;
    });

    // Convert operator projects Set to count
    Object.keys(operatorStats).forEach(operator => {
      operatorStats[operator].projectCount = operatorStats[operator].projects.size;
      delete operatorStats[operator].projects;
    });

    // Atomic test coverage
    const techniqueIds = [...new Set(actions.filter(a => a.mitreAttackTechnique).map(a => a.mitreAttackTechnique.id))];
    const atomicCoverage = {
      totalTechniques: techniqueIds.length,
      techniquesWithAtomics: [...new Set(atomicTests.filter(a => techniqueIds.includes(a.techniqueId)).map(a => a.techniqueId))].length,
      totalAtomicTests: atomicTests.filter(a => techniqueIds.includes(a.techniqueId)).length,
      coveragePercentage: techniqueIds.length > 0 
        ? ((atomicTests.filter(a => techniqueIds.includes(a.techniqueId)).length / techniqueIds.length) * 100).toFixed(1)
        : 0
    };

    return {
      projectStatusCount,
      actionResultCount,
      tacticCount,
      timelineData,
      operatorStats,
      projectActionCount,
      atomicCoverage,
      totalProjects: projects.length,
      totalActions: actions.length,
      activeProjects: projects.filter(p => p.status === 'in-progress').length,
      completedProjects: projects.filter(p => p.status === 'completed').length,
      globalSuccessRate: ((actionResultCount.successful / actions.length) * 100).toFixed(1),
      globalDetectionRate: ((actionResultCount.detected / actions.length) * 100).toFixed(1),
      averageActionsPerProject: (actions.length / projects.length).toFixed(1),
      topOperators: Object.entries(operatorStats)
        .sort((a, b) => b[1].total - a[1].total)
        .slice(0, 5),
      topProjects: projects
        .map(project => ({
          ...project,
          actionCount: projectActionCount[project._id] || 0
        }))
        .sort((a, b) => b.actionCount - a.actionCount)
        .slice(0, 5)
    };
  };

  // Chart configurations
  const getProjectStatusChart = (analytics) => ({
    labels: ['Planning', 'In Progress', 'Completed', 'Archived'],
    datasets: [{
      data: [
        analytics.projectStatusCount.planning,
        analytics.projectStatusCount['in-progress'],
        analytics.projectStatusCount.completed,
        analytics.projectStatusCount.archived,
      ],
      backgroundColor: [
        'rgba(251, 191, 36, 0.8)',   // yellow - planning
        'rgba(59, 130, 246, 0.8)',   // blue - in-progress
        'rgba(34, 197, 94, 0.8)',    // green - completed
        'rgba(156, 163, 175, 0.8)',  // gray - archived
      ],
      borderColor: [
        'rgba(251, 191, 36, 1)',
        'rgba(59, 130, 246, 1)',
        'rgba(34, 197, 94, 1)',
        'rgba(156, 163, 175, 1)',
      ],
      borderWidth: 1,
    }],
  });

  const getActionResultsChart = (analytics) => ({
    labels: ['Successful', 'Detected', 'Blocked', 'Failed', 'Partial', 'Unknown'],
    datasets: [{
      data: [
        analytics.actionResultCount.successful,
        analytics.actionResultCount.detected,
        analytics.actionResultCount.blocked,
        analytics.actionResultCount.failed,
        analytics.actionResultCount.partial,
        analytics.actionResultCount.unknown,
      ],
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)',   // green
        'rgba(251, 191, 36, 0.8)',  // yellow
        'rgba(239, 68, 68, 0.8)',   // red
        'rgba(156, 163, 175, 0.8)', // gray
        'rgba(59, 130, 246, 0.8)',  // blue
        'rgba(147, 51, 234, 0.8)',  // purple
      ],
      borderColor: [
        'rgba(34, 197, 94, 1)',
        'rgba(251, 191, 36, 1)',
        'rgba(239, 68, 68, 1)',
        'rgba(156, 163, 175, 1)',
        'rgba(59, 130, 246, 1)',
        'rgba(147, 51, 234, 1)',
      ],
      borderWidth: 1,
    }],
  });

  const getMitreTacticsChart = (analytics) => ({
    labels: Object.keys(analytics.tacticCount),
    datasets: [{
      label: 'Actions by MITRE Tactic',
      data: Object.values(analytics.tacticCount),
      backgroundColor: 'rgba(147, 51, 234, 0.6)',
      borderColor: 'rgba(147, 51, 234, 1)',
      borderWidth: 1,
    }],
  });

  const getTimelineChart = (analytics) => {
    const sortedMonths = Object.keys(analytics.timelineData).sort((a, b) => new Date(a) - new Date(b));
    
    return {
      labels: sortedMonths,
      datasets: [{
        label: 'Actions per Month',
        data: sortedMonths.map(month => analytics.timelineData[month]),
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      }],
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} border px-4 py-3 rounded`}>
        {error}
      </div>
    );
  }

  const analytics = getGlobalAnalytics();

  if (!analytics) {
    return (
      <div>
        <h2 className={`text-2xl font-bold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <FontAwesomeIcon icon={faChartBar} className="mr-2 text-primary" />
          {t('reports.title')}
        </h2>
        <div className={`${darkMode ? 'bg-yellow-900/30 border-yellow-800' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-6 text-center`}>
          <FontAwesomeIcon icon={faChartPie} className={`text-6xl mb-4 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>{t('reports.noDataAvailable')}</h3>
          <p className={`mb-4 ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
            {t('reports.noDataDescription')}
          </p>
          <Link
            to="/projects/new"
            className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 inline-flex items-center"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            {t('reports.createFirstProject')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('reports.title')}
        subtitle={t('reports.subtitle')}
        icon={<FontAwesomeIcon icon={faChartBar} />}
      />

      {/* Global Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faFolder} className="text-3xl mr-4 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-blue-600">{analytics.totalProjects}</p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('executive.totalProjects')}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{analytics.activeProjects} {t('reports.active')}</p>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faBullseye} className="text-3xl mr-4 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-green-600">{analytics.totalActions}</p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('executive.totalActions')}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('reports.avgPerProject', { avg: analytics.averageActionsPerProject })}</p>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCheckCircle} className="text-3xl mr-4 text-emerald-600" />
            <div>
              <p className="text-2xl font-bold text-emerald-600">{analytics.globalSuccessRate}%</p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('reports.successRate')}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('reports.acrossOperations')}</p>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faMapMarkedAlt} className="text-3xl mr-4 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-purple-600">{Object.keys(analytics.tacticCount).length}</p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('reports.mitreTactics')}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('reports.coverageAchieved')}</p>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faFlask} className="text-3xl mr-4 text-orange-600" />
            <div>
              <p className="text-2xl font-bold text-orange-600">{analytics.atomicCoverage.coveragePercentage}%</p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('executive.atomicCoverage')}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{analytics.atomicCoverage.techniquesWithAtomics}/{analytics.atomicCoverage.totalTechniques} {t('reports.techniques')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md overflow-hidden`}>
        <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="flex">
            <button
              className={`px-6 py-3 text-sm font-medium flex items-center ${
                activeTab === 'overview'
                  ? `border-b-2 border-blue-500 text-blue-600 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`
                  : `${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`
              }`}
              onClick={() => setActiveTab('overview')}
            >
              <FontAwesomeIcon icon={faChartLine} className="mr-2" />
              {t('reports.analyticsOverview')}
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium flex items-center ${
                activeTab === 'mitre'
                  ? `border-b-2 border-blue-500 text-blue-600 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`
                  : `${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`
              }`}
              onClick={() => setActiveTab('mitre')}
            >
              <FontAwesomeIcon icon={faMapMarkedAlt} className="mr-2" />
              {t('reports.globalMitreHeatmap')}
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium flex items-center ${
                activeTab === 'atomic'
                  ? `border-b-2 border-blue-500 text-blue-600 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`
                  : `${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`
              }`}
              onClick={() => setActiveTab('atomic')}
            >
              <FontAwesomeIcon icon={faFlask} className="mr-2" />
              {t('reports.atomicTestLibrary')}
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium flex items-center ${
                activeTab === 'detailed'
                  ? `border-b-2 border-blue-500 text-blue-600 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`
                  : `${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`
              }`}
              onClick={() => setActiveTab('detailed')}
            >
              <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
              {t('reports.detailedAnalysis')}
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('reports.projectStatusDistribution')}</h3>
                  <div style={{ height: '300px', position: 'relative' }}>
                    <Pie 
                      data={getProjectStatusChart(analytics)} 
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom' },
                        },
                      }}
                    />
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('reports.globalActionResults')}</h3>
                  <div style={{ height: '300px', position: 'relative' }}>
                    <Pie 
                      data={getActionResultsChart(analytics)} 
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom' },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Charts Row 2 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('reports.mitreTacticsUsage')}</h3>
                  <div style={{ height: '300px', position: 'relative' }}>
                    <Bar
                      data={getMitreTacticsChart(analytics)}
                      options={{
                        maintainAspectRatio: false,
                        scales: {
                          y: { beginAtZero: true, ticks: { precision: 0 } }
                        },
                        plugins: {
                          legend: { display: false },
                        },
                      }}
                    />
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('reports.activityTimeline')}</h3>
                  <div style={{ height: '300px', position: 'relative' }}>
                    <Line
                      data={getTimelineChart(analytics)}
                      options={{
                        maintainAspectRatio: false,
                        scales: {
                          y: { beginAtZero: true, ticks: { precision: 0 } }
                        },
                        plugins: {
                          legend: { display: false },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Top Performers Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('reports.topOperators')}</h3>
                  <div className="space-y-3">
                    {analytics.topOperators.map(([operator, stats], index) => (
                      <div key={operator} className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded-lg flex justify-between items-center`}>
                        <div className="flex items-center">
                          <FontAwesomeIcon
                            icon={index === 0 ? faTrophy : index === 1 ? faMedal : index === 2 ? faAward : faUser}
                            className={`text-2xl mr-3 ${
                              index === 0 ? 'text-yellow-500' :
                              index === 1 ? 'text-gray-400' :
                              index === 2 ? 'text-orange-600' :
                              'text-blue-600'
                            }`}
                          />
                          <div>
                            <p className={`font-medium ${darkMode ? 'text-white' : ''}`}>{operator}</p>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {stats.total} actions • {stats.projectCount} projects
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">
                            {((stats.successful / stats.total) * 100).toFixed(1)}%
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Success Rate</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('reports.mostActiveProjects')}</h3>
                  <div className="space-y-3">
                    {analytics.topProjects.map((project, index) => (
                      <div key={project._id} className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded-lg`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <Link
                              to={`/projects/${project._id}`}
                              className="font-medium text-blue-600 hover:text-blue-800"
                            >
                              {project.name}
                            </Link>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{project.client}</p>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                              project.status === 'completed' ? 'bg-green-100 text-green-800' :
                              project.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                              project.status === 'planning' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {project.status}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-purple-600">{project.actionCount}</p>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Actions</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MITRE Heatmap Tab */}
          {activeTab === 'mitre' && (
            <div>
              <MitreHeatmap
                actions={actions}
                title="Global MITRE ATT&CK Coverage (All Projects)"
                webFindings={webFindingsForMitre}
              />
              
              {/* Additional MITRE Statistics */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className={`${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'} rounded-lg p-6`}>
                  <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>Most Used Techniques</h4>
                  <div className="space-y-2">
                    {Object.entries(
                      actions
                        .filter(a => a.mitreAttackTechnique)
                        .reduce((acc, action) => {
                          const id = action.mitreAttackTechnique.id;
                          acc[id] = (acc[id] || 0) + 1;
                          return acc;
                        }, {})
                    )
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([techniqueId, count]) => {
                        const technique = actions.find(a => a.mitreAttackTechnique?.id === techniqueId)?.mitreAttackTechnique;
                        return (
                          <div key={techniqueId} className="flex justify-between items-center">
                            <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : ''}`}>{techniqueId}</span>
                            <span className="text-sm text-purple-600">{count}x</span>
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div className={`${darkMode ? 'bg-green-900/30' : 'bg-green-50'} rounded-lg p-6`}>
                  <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>High Success Tactics</h4>
                  <div className="space-y-2">
                    {Object.entries(analytics.tacticCount)
                      .map(([tactic, count]) => {
                        const successfulInTactic = actions.filter(
                          a => a.mitreAttackTechnique?.tactic === tactic && a.result === 'successful'
                        ).length;
                        return {
                          tactic,
                          count,
                          successRate: (successfulInTactic / count) * 100
                        };
                      })
                      .sort((a, b) => b.successRate - a.successRate)
                      .slice(0, 5)
                      .map(({ tactic, successRate }) => (
                        <div key={tactic} className="flex justify-between items-center">
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : ''}`}>{tactic}</span>
                          <span className="text-sm text-green-600">{successRate.toFixed(1)}%</span>
                        </div>
                      ))}
                  </div>
                </div>

                <div className={`${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'} rounded-lg p-6`}>
                  <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>Detection Hotspots</h4>
                  <div className="space-y-2">
                    {Object.entries(analytics.tacticCount)
                      .map(([tactic, count]) => {
                        const detectedInTactic = actions.filter(
                          a => a.mitreAttackTechnique?.tactic === tactic && a.result === 'detected'
                        ).length;
                        return {
                          tactic,
                          count,
                          detectionRate: (detectedInTactic / count) * 100
                        };
                      })
                      .sort((a, b) => b.detectionRate - a.detectionRate)
                      .slice(0, 5)
                      .map(({ tactic, detectionRate }) => (
                        <div key={tactic} className="flex justify-between items-center">
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : ''}`}>{tactic}</span>
                          <span className="text-sm text-yellow-600">{detectionRate.toFixed(1)}%</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Atomic Test Library Tab */}
          {activeTab === 'atomic' && (
            <div className="space-y-6">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : ''}`}>{t('reports.atomicTestLibrary')}</h3>

              <div className={`${darkMode ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4 mb-6`}>
                <h4 className={`font-medium mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>Atomic Test Coverage Summary</h4>
                <p className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                  {analytics.atomicCoverage.techniquesWithAtomics} of {analytics.atomicCoverage.totalTechniques} techniques used have atomic tests available.
                  Total of {analytics.atomicCoverage.totalAtomicTests} tests are relevant to your operations.
                </p>
              </div>

              {/* Use AtomicTestViewer component instead of inline display */}
              {atomicTests.length === 0 ? (
                <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-6 text-center`}>
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No atomic tests available in the database.</p>
                </div>
              ) : (
                <AtomicTestViewer 
                  atomicTests={atomicTests}
                  actions={actions}
                />
              )}
            </div>
          )}

          {/* Detailed Analysis Tab */}
          {activeTab === 'detailed' && (
            <div className="space-y-8">
              {/* All Operators Performance */}
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('reports.operatorPerformance')}</h3>
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Operator
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Projects
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Total Actions
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Successful
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Detected
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Success Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                      {Object.entries(analytics.operatorStats)
                        .sort((a, b) => b[1].total - a[1].total)
                        .map(([operator, stats]) => (
                        <tr key={operator}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {operator}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {stats.projectCount}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {stats.total}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {stats.successful}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {stats.detected}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <div className="flex items-center">
                              <span className="mr-2">
                                {((stats.successful / stats.total) * 100).toFixed(1)}%
                              </span>
                              <div className={`w-16 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} rounded-full h-2`}>
                                <div
                                  className="bg-green-500 h-2 rounded-full"
                                  style={{ width: `${(stats.successful / stats.total) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* All Projects Summary */}
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('reports.allProjectsSummary')}</h3>
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Project
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Client
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Status
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Actions
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Start Date
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                      {projects
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                        .map((project) => (
                        <tr key={project._id}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            <Link
                              to={`/projects/${project._id}`}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              {project.name}
                            </Link>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {project.client}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              project.status === 'completed' ? 'bg-green-100 text-green-800' :
                              project.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                              project.status === 'planning' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {project.status}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {analytics.projectActionCount[project._id] || 0}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {new Date(project.startDate).toLocaleDateString()}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <div className="flex space-x-2">
                              <Link
                                to={`/projects/${project._id}`}
                                className="text-blue-600 hover:text-blue-800 flex items-center"
                              >
                                <FontAwesomeIcon icon={faEye} className="mr-1" />
                                View
                              </Link>
                              <Link
                                to={`/projects/${project._id}/reports`}
                                className="text-purple-600 hover:text-purple-800 flex items-center"
                              >
                                <FontAwesomeIcon icon={faChartBar} className="mr-1" />
                                Reports
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Export Section */}
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-6`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('reports.exportGlobalReports')}</h3>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={handleExportPDF}
                    disabled={exporting}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center"
                  >
                    <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                    {t('reports.exportExecutiveSummary')}
                  </button>
                  <button
                    onClick={handleExportExcel}
                    disabled={exporting}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-green-400 flex items-center"
                  >
                    <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                    {t('reports.exportExcel')}
                  </button>
                  <button
                    onClick={handleExportMitreNavigator}
                    disabled={exporting}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:bg-purple-400 flex items-center"
                  >
                    <FontAwesomeIcon icon={faMap} className="mr-2" />
                    {t('reports.exportMitreNavigator')}
                  </button>
                  <button
                    onClick={handleExportAnalytics}
                    disabled={exporting}
                    className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 disabled:bg-orange-400 flex items-center"
                  >
                    <FontAwesomeIcon icon={faChartArea} className="mr-2" />
                    {t('reports.exportAnalytics')}
                  </button>
                </div>
                <p className={`text-sm mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {exporting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      {t('common.exporting')}
                    </>
                  ) : (
                    t('reports.exportDescription')
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;